// Libraries
import * as React from "react";

// Utils
import {
  Action,
  FlexTrackingInfo,
  sendDelayedTrackEvent,
  sendImmediateTrackEvent
} from "components/utility/analytics/FlexAnalyticsUtils";

// Types
import { ModuleMapProps } from "../typings";

/**
 * Custom hook to control the fullscreen map dialog behavior
 * @param props
 */
export const useFullscreenOpen = (props: ModuleMapProps) => {
  const isPwaDialogMapQuery = (value: string): boolean => {
    const pwaDialogMap =
      props.context && props.context.queryParams && props.context.queryParams.pwaDialogMap;

    return pwaDialogMap === value;
  };

  const [showFullscreenMap, setShowFullscreenMap] = React.useState(
    isPwaDialogMapQuery(`open-${props.flexComponent.id}`)
  );
  // Since the state cannot be used from closure, we need to use a Ref
  const showFullscreenMapRef = React.useRef(showFullscreenMap);
  showFullscreenMapRef.current = showFullscreenMap;

  const toggleShowFullscreenMap = React.useCallback(
    () => {
      const routeStore = props.routeStore;

      const flexTrackingInfo: FlexTrackingInfo = {
        moduleName: props.flexComponent.name,
        rfrr: !showFullscreenMapRef.current ? undefined : "close",
        action: Action.CLICK
      };

      sendDelayedTrackEvent(flexTrackingInfo, props.analytics);

      if (routeStore) {
        routeStore.updateSearch({
          pwaDialogMap: showFullscreenMapRef.current
            ? `close-${props.flexComponent.id}`
            : `open-${props.flexComponent.id}`
        });
      }

      setShowFullscreenMap(!showFullscreenMapRef.current);
    },
    [showFullscreenMapRef.current]
  );

  const sendUserToHSR = () => {
    const flexTrackingInfo: FlexTrackingInfo = {
      moduleName: props.flexComponent.name,
      rfrr: "toHSR",
      action: Action.CLICK
    };

    sendImmediateTrackEvent(flexTrackingInfo, props.analytics);
    const regionId =
      props.context && props.context.searchContext && props.context.searchContext.location.id;
    const langId = props.context && props.context && props.context.langId;
    if (regionId && langId) {
      window.location.href = `/Hotel-Search?GOTO=HOTSEARCH&SearchArea=City&SearchType=Place&adults=2&children=&destination=&lang=${langId}&needUTF8Decode=true&pwaOverlay=map&regionId=${regionId}&rfrr=hotel.search`;
    }
  };

  // Handles back button
  React.useEffect(() => {
    const listenToBackButton = () => {
      if (showFullscreenMapRef.current) {
        toggleShowFullscreenMap();
      }
    };

    window.addEventListener("popstate", listenToBackButton);

    return () => window.removeEventListener("popstate", listenToBackButton);
  }, []);

  return {
    isPwaDialogMapQuery,
    toggleShowFullscreenMap,
    sendUserToHSR
  };
};

export default useFullscreenOpen;
